import type { InternalAxiosRequestConfig } from 'axios'

export const INTERNAL_DATA_HEADER = 'x-internal-data'

const AUDIOFORM_PATH = '/audioforms'

export interface InternalData {
  workflow_name: string
  module_name: string
  workflow_session_id: string
  module_session_id: string
}

export type InternalDataInput = Partial<InternalData>

export type InternalDataProvider = () => InternalDataInput | undefined

let internalDataProvider: InternalDataProvider | undefined = undefined

export const setInternalDataProvider = (
  provider: InternalDataProvider | undefined
): void => {
  internalDataProvider = provider
}

const normalizeInternalData = (
  data: InternalDataInput | undefined
): InternalData | undefined => {
  if (!data) return undefined

  const normalized: InternalData = {
    workflow_name: data.workflow_name ?? '',
    module_name: data.module_name ?? '',
    workflow_session_id: data.workflow_session_id ?? '',
    module_session_id: data.module_session_id ?? '',
  }

  const hasValue = [
    normalized.workflow_name,
    normalized.module_name,
    normalized.workflow_session_id,
    normalized.module_session_id,
  ].some((value) => value.length > 0)

  return hasValue ? normalized : undefined
}

export const encodeInternalDataHeader = (
  data: InternalDataInput | undefined
): string | undefined => {
  const normalized = normalizeInternalData(data)
  if (!normalized) return undefined

  // The backends read the header keys in kebab-case.
  return btoa(
    JSON.stringify({
      'workflow-name': normalized.workflow_name,
      'module-name': normalized.module_name,
      'workflow-session-id': normalized.workflow_session_id,
      'module-session-id': normalized.module_session_id,
    })
  )
}

export const getInternalDataHeader = (): string | undefined =>
  encodeInternalDataHeader(internalDataProvider?.())

export const isAudioformRequest = (config: {
  url?: string | undefined
  baseURL?: string | undefined
}): boolean =>
  (config.baseURL ?? '').includes(AUDIOFORM_PATH) ||
  (config.url ?? '').includes(AUDIOFORM_PATH)

export const attachInternalDataHeader = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (!isAudioformRequest(config)) return config

  if (config.headers.get(INTERNAL_DATA_HEADER)) return config

  const headerValue = getInternalDataHeader()
  if (!headerValue) return config

  config.headers.set(INTERNAL_DATA_HEADER, headerValue)

  return config
}
